import { create } from 'zustand';
import { supabase } from '@/lib/supabase';
import type { DocumentCategory } from '@/types';

export type UploadType = 'image' | 'document';

export interface UploadItem {
  id: string;
  type: UploadType;
  fileName: string;
  propertyId: string;
  category?: DocumentCategory; // nur für Dokumente
  progress: number;
  status: 'pending' | 'uploading' | 'processing' | 'done' | 'error';
  error?: string;
  previewUrl?: string; // nur für Bilder
  file: File;
  createdAt: number;
}

interface UploadState {
  uploads: UploadItem[];
  isMinimized: boolean;

  // Actions
  addUpload: (item: Omit<UploadItem, 'id' | 'status' | 'progress' | 'createdAt'>) => string;
  updateUpload: (id: string, updates: Partial<UploadItem>) => void;
  removeUpload: (id: string) => void;
  clearCompleted: () => void;
  setMinimized: (minimized: boolean) => void;

  // Upload ausführen
  processUpload: (id: string) => Promise<boolean>;
  processAllPending: () => void;
}

const MAX_CONCURRENT_UPLOADS = 3;
const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'gif'];
const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_DOC_SIZE = 20 * 1024 * 1024; // 20MB

export const useUploadStore = create<UploadState>((set, get) => ({
  uploads: [],
  isMinimized: false,

  addUpload: (item) => {
    const id = `upload-${Date.now()}-${Math.random().toString(36).substring(7)}`;
    const newItem: UploadItem = {
      ...item,
      id,
      status: 'pending',
      progress: 0,
      createdAt: Date.now(),
    };

    set(state => ({
      uploads: [...state.uploads, newItem],
    }));

    // Automatisch starten wenn weniger als MAX_CONCURRENT_UPLOADS aktiv
    setTimeout(() => get().processAllPending(), 0);

    return id;
  },

  updateUpload: (id, updates) => {
    set(state => ({
      uploads: state.uploads.map(u =>
        u.id === id ? { ...u, ...updates } : u
      ),
    }));
  },

  removeUpload: (id) => {
    const upload = get().uploads.find(u => u.id === id);
    if (upload?.previewUrl) {
      URL.revokeObjectURL(upload.previewUrl);
    }
    set(state => ({
      uploads: state.uploads.filter(u => u.id !== id),
    }));
  },

  clearCompleted: () => {
    const completed = get().uploads.filter(u => u.status === 'done' || u.status === 'error');
    completed.forEach(u => {
      if (u.previewUrl) URL.revokeObjectURL(u.previewUrl);
    });
    set(state => ({
      uploads: state.uploads.filter(u => u.status !== 'done' && u.status !== 'error'),
    }));
  },

  setMinimized: (minimized) => {
    set({ isMinimized: minimized });
  },

  processUpload: async (id) => {
    const { uploads, updateUpload } = get();
    const upload = uploads.find(u => u.id === id);

    if (!upload || upload.status !== 'pending') return false;

    updateUpload(id, { status: 'uploading', progress: 5 });

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Nicht angemeldet');

      // Validierung
      const maxSize = upload.type === 'image' ? MAX_IMAGE_SIZE : MAX_DOC_SIZE;
      if (upload.file.size > maxSize) {
        throw new Error(`Maximale Dateigröße: ${maxSize / 1024 / 1024}MB`);
      }

      if (upload.type === 'image') {
        const ext = upload.file.name.split('.').pop()?.toLowerCase();
        if (!ext || !IMAGE_EXTENSIONS.includes(ext)) {
          throw new Error(`Nur folgende Formate: ${IMAGE_EXTENSIONS.join(', ')}`);
        }
      }

      updateUpload(id, { progress: 10 });

      // Dateiname und Pfad
      const ext = upload.file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${ext}`;

      let storagePath: string;
      let bucketName: string;
      let tableName: string;

      if (upload.type === 'image') {
        bucketName = 'property-images';
        tableName = 'property_images';
        storagePath = `${upload.propertyId}/${fileName}`;
      } else {
        bucketName = 'documents';
        tableName = 'documents';
        storagePath = `${upload.propertyId}/${upload.category}/${fileName}`;
      }

      // Upload zu Storage
      const { error: uploadError } = await supabase.storage
        .from(bucketName)
        .upload(storagePath, upload.file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) throw uploadError;

      updateUpload(id, { progress: 70, status: 'processing' });

      // DB-Eintrag erstellen
      if (upload.type === 'image') {
        const { error: dbError } = await supabase
          .from(tableName)
          .insert({
            property_id: upload.propertyId,
            storage_path: storagePath,
            file_name: upload.file.name,
            file_size: upload.file.size,
            is_primary: false,
            uploaded_by: user.id,
          });

        if (dbError) throw dbError;
      } else {
        const { error: dbError } = await supabase
          .from(tableName)
          .insert({
            property_id: upload.propertyId,
            category: upload.category,
            name: upload.file.name,
            storage_path: storagePath,
            file_size: upload.file.size,
            mime_type: upload.file.type,
            uploaded_by: user.id,
          });

        if (dbError) throw dbError;
      }

      updateUpload(id, { progress: 100, status: 'done' });

      // Preview URL aufräumen
      if (upload.previewUrl) {
        URL.revokeObjectURL(upload.previewUrl);
      }

      // Nach Erfolg nächsten Upload starten
      setTimeout(() => get().processAllPending(), 100);

      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Upload fehlgeschlagen';
      console.error('Upload error:', err);
      updateUpload(id, { status: 'error', error: message });

      // Auch bei Fehler nächsten Upload starten
      setTimeout(() => get().processAllPending(), 100);

      return false;
    }
  },

  processAllPending: () => {
    const { uploads, processUpload } = get();

    // Zähle aktive Uploads
    const activeCount = uploads.filter(
      u => u.status === 'uploading' || u.status === 'processing'
    ).length;

    // Starte pending Uploads bis zum Limit
    const pending = uploads.filter(u => u.status === 'pending');
    const toStart = pending.slice(0, MAX_CONCURRENT_UPLOADS - activeCount);

    toStart.forEach(upload => {
      processUpload(upload.id);
    });
  },
}));

// Selektoren
export const selectActiveUploads = (state: UploadState) =>
  state.uploads.filter(u => u.status !== 'done');

export const selectHasActiveUploads = (state: UploadState) =>
  state.uploads.some(u => u.status === 'pending' || u.status === 'uploading' || u.status === 'processing');

export const selectCompletedCount = (state: UploadState) =>
  state.uploads.filter(u => u.status === 'done').length;

export const selectErrorCount = (state: UploadState) =>
  state.uploads.filter(u => u.status === 'error').length;
